import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import {
  ListRequest,
  useHtzConfirmationListQuery,
} from "@/services/backend/htz/confirmations/api/confirmation-api";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";

export function ConfirmationTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (confirmationId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useHtzConfirmationListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Auftragsbestätugungsdatum")}</TableHead>
              <TableHead>{t("Heruntergeladen")}</TableHead>
              <TableHead className="w-32">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error && <TableRtkErrorRow error={error} colSpan={5} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={5}
                text={t("Keine Auftragsbestätugunge gefunden")}
              />
            )}
            {list &&
              list.items.map((confirmation) => (
                <ConfirmationTableRow
                  key={confirmation.id}
                  confirmation={confirmation}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function ConfirmationTableRow({
  confirmation,
  renderRowLink,
}: {
  confirmation: Confirmation;
  renderRowLink: (confirmationId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          confirmation.id,
          <DocumentNumberById documentId={confirmation.id} />,
        )}
      </TableCell>
      <TableCell>
        {formatDate(confirmation.createdAt)},{" "}
        {formatTime(confirmation.createdAt)}
      </TableCell>
      <TableCell>
        {confirmation.date && formatDate(confirmation.date)}
      </TableCell>
      <TableCell>{confirmation.immutable ? "ja" : "nein"}</TableCell>
      <TableCell>
        <div className="flex space-x-2">
          {renderRowLink(
            confirmation.id,
            <Button variant="ghost">
              <Search className="h-4 w-4" />
            </Button>,
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ConfirmationTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultHtzConfirmationListRequest(
  processId: string,
): ListRequest {
  return {
    process: { active: true, values: [processId] },
    customer: { active: false, values: [] },
    immutable: { active: false, values: [] },
    orderBy: [{ field: "createdAt" }],
    limit: 50,
    offset: 0,
  };
}
